import React from 'react';
const CommonPagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100, 250],
  loading = false,
  style = {},
  className = ''
}) => {
  const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const safeTotalPages = Math.max(1, totalPages || 1);
  const startItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalItems);

  // Generate page numbers array with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (safeTotalPages <= maxVisible + 2) {
      for (let i = 1; i <= safeTotalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, safePage - 1);
      let end = Math.min(safeTotalPages - 1, safePage + 1);

      if (safePage <= 3) {
        end = 4;
      } else if (safePage >= safeTotalPages - 2) {
        start = safeTotalPages - 3;
      }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < safeTotalPages - 1) pages.push('...');
      pages.push(safeTotalPages);
    }
    return pages;
  };

  return (
    <div
      className={`common-pagination-container ${className}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '12px 16px',
        background: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        borderRadius: '0 0 8px 8px',
        fontSize: '13px',
        color: '#475569',
        opacity: loading ? 0.6 : 1,
        pointerEvents: loading ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
        ...style
      }}
    >
      {/* Left side: Range description & page size selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <span>
          Showing <strong style={{ color: '#0f172a' }}>{startItem}</strong> to{' '}
          <strong style={{ color: '#0f172a' }}>{endItem}</strong> of{' '}
          <strong style={{ color: '#2563eb' }}>{totalItems.toLocaleString()}</strong> entries
        </span>

        {onPageSizeChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label htmlFor="common-page-size-select" style={{ fontSize: '12px', color: '#64748b' }}>Per page:</label>
            <select
              id="common-page-size-select"
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                onPageSizeChange(newSize);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                fontSize: '12px',
                color: '#0f172a',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right side: Page Navigation buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* First page button */}
        <button
          type="button"
          onClick={() => onPageChange && onPageChange(1)}
          disabled={safePage <= 1 || loading}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid #cbd5e1',
            background: safePage <= 1 ? '#f1f5f9' : '#ffffff',
            color: safePage <= 1 ? '#94a3b8' : '#334155',
            cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.15s ease'
          }}
          title="First Page"
        >
          «
        </button>

        {/* Prev page button */}
        <button
          type="button"
          onClick={() => onPageChange && onPageChange(safePage - 1)}
          disabled={safePage <= 1 || loading}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #cbd5e1',
            background: safePage <= 1 ? '#f1f5f9' : '#ffffff',
            color: safePage <= 1 ? '#94a3b8' : '#334155',
            cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.15s ease'
          }}
        >
          Prev
        </button>

        {/* Page number buttons */}
        {getPageNumbers().map((p, idx) => {
          if (p === '...') {
            return (
              <span key={`ellipsis-${idx}`} style={{ padding: '0 6px', color: '#94a3b8' }}>
                ...
              </span>
            );
          }
          const isActive = p === safePage;
          return (
            <button
              key={`page-${p}`}
              type="button"
              onClick={() => onPageChange && onPageChange(p)}
              disabled={loading}
              style={{
                padding: '6px 11px',
                borderRadius: '6px',
                border: isActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                background: isActive ? '#2563eb' : '#ffffff',
                color: isActive ? '#ffffff' : '#334155',
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.15s ease'
              }}
            >
              {p}
            </button>
          );
        })}

        {/* Next page button */}
        <button
          type="button"
          onClick={() => onPageChange && onPageChange(safePage + 1)}
          disabled={safePage >= safeTotalPages || loading}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #cbd5e1',
            background: safePage >= safeTotalPages ? '#f1f5f9' : '#ffffff',
            color: safePage >= safeTotalPages ? '#94a3b8' : '#334155',
            cursor: safePage >= safeTotalPages ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.15s ease'
          }}
        >
          Next
        </button>

        {/* Last page button */}
        <button
          type="button"
          onClick={() => onPageChange && onPageChange(safeTotalPages)}
          disabled={safePage >= safeTotalPages || loading}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid #cbd5e1',
            background: safePage >= safeTotalPages ? '#f1f5f9' : '#ffffff',
            color: safePage >= safeTotalPages ? '#94a3b8' : '#334155',
            cursor: safePage >= safeTotalPages ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.15s ease'
          }}
          title="Last Page"
        >
          »
        </button>
      </div>
    </div>
  );
};

export default CommonPagination;
